import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import {
	clearSelectedRecords,
	setSelectedRecords,
	selectRecordIds,
	type BaseRecord,
} from '../../redux/slices/selectedGridRecordsSlice';
import {
	type GridColDef,
	type GridRowSelectionModel,
	type GridDataSource,
	GridGetRowsError,
	type GridUpdateRowError,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import TableViewSelector, { type SECTION_CHANGE } from './TableViewSelector/TableViewSelector';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ListViewCell } from './MobileListView/MobileListViewCell';
import DataGridToolbarComponent from './DataGridToolbarComponent';
import type { RootState } from '../../redux/store';
import { useEffect, useState, useMemo } from 'react';
import { showAlert } from '../../redux/slices/alertSlice';
import type { DataGridType } from './DataGridTypes.ts';

interface DataGridProComponentProps {
	columns: GridColDef[];
	dataGridType: DataGridType;
	checkboxEnabled?: boolean;
	page?: number;
	pageSize?: number;
	dataSource?: GridDataSource;
	dataIsLoading?: boolean;
	onRefresh?: () => void;
	onSectionChange?: (action: SECTION_CHANGE) => void;
	sectionPosition?: number;
	refreshTrigger?: number;
}

const DataGridProComponent = ({
	columns = [],
	dataGridType,
	checkboxEnabled = true,
	dataSource,
	dataIsLoading = false,
	onRefresh,
	onSectionChange,
	sectionPosition,
	refreshTrigger,
}: DataGridProComponentProps) => {
	const apiRef = useGridApiRef();
	const dispatch = useDispatch();

	// Get selected IDs directly using the selector
	const selectedIds = useSelector((state: RootState) => selectRecordIds(state));
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
		type: 'include',
		ids: new Set(selectedIds),
	});

	// local state keys off of selectedIds from the store
	useEffect(() => {
		setRowSelectionModel({
			type: 'include',
			ids: new Set(selectedIds),
		});
	}, [selectedIds]);

	// selectedRecords is a single global slice; clear it on unmount so a stale
	// selection from this grid doesn't leak into whichever grid mounts next
	// (e.g. switching tabs or navigating away).
	useEffect(() => {
		return () => {
			dispatch(clearSelectedRecords());
		};
	}, [dispatch]);

	// Server-side dataSource caches the current page and doesn't react to a
	// dataSource prop reference swap — must imperatively clear + refetch.
	useEffect(() => {
		if (refreshTrigger === undefined || refreshTrigger === 0) return;
		apiRef.current?.dataSource?.cache?.clear();
		apiRef.current?.dataSource?.fetchRows();
	}, [refreshTrigger, apiRef]);

	const isMobile = useMediaQuery('(max-width:1027px)');

	const showTableViewSelector = () => dataGridType.showTableViewSelector && !isMobile;

	const stableColumns = useMemo<GridColDef[]>(() => {
		return columns.map((col) => ({
			// ensure each column has a unique, stable field
			...col,
			// optional: enforce minWidth for flex columns to avoid jitter
			minWidth: col.minWidth ?? (col.width ? col.width : 120),
		}));
	}, [columns]);

	const getCurrentRows = (): BaseRecord[] => {
		const rowModels = apiRef.current?.getRowModels();
		return rowModels ? Array.from(rowModels.values()).map((row) => row as BaseRecord) : [];
	};

	// Abstracted handler for row selection changes
	const handleRowSelectionModelChange = (newSelectionModel: any) => {
		// Select All condition
		if (newSelectionModel.ids.size === 0 && selectedIds.length === 0) {
			dispatch(setSelectedRecords(getCurrentRows()));
			return;
		}

		// Deselect All condition
		if (newSelectionModel.ids.size === 0 && selectedIds.length > 0) {
			setRowSelectionModel({ type: 'include', ids: new Set([]) });
			dispatch(clearSelectedRecords());
			return;
		}

		const selectedRowIds: Set<string | number> = new Set(Array.from(newSelectionModel.ids));

		setRowSelectionModel(newSelectionModel);

		// Map ids to row data
		const selected = getCurrentRows()
			.filter((row: any) => selectedRowIds.has(row.id))
			.map((row: any) => ({
				...row,
				type: dataGridType.title, // Using type instead of entityType for consistency with selectors
			}));

		// Update store with selected records
		dispatch(clearSelectedRecords());
		if (selected.length > 0) {
			dispatch(setSelectedRecords(selected));
		}
	};

	const mobileListViewColDef = {
		field: 'listColumn',
		renderCell: (params: any) => <ListViewCell {...params} dataGridType={dataGridType} />,
	};

	return (
		<Box
			sx={{
				width: '100%',
				mt: 3,
				padding: 0,
				minWidth: 320, // Prevents collapse
				overflowX: 'auto', // Enables horizontal scroll if needed
				zIndex: 100,
				'& .MuiDataGrid-virtualScroller ': {
					zIndex: 100,
				},
			}}
			data-testid="data-grid-pro"
		>
			{/* Solid bar above DataGrid */}
			<DataGridToolbarComponent apiRef={apiRef} dataGridType={dataGridType} onRefresh={onRefresh} />
			{showTableViewSelector() && (
				<TableViewSelector
					type={dataGridType}
					onSectionChange={onSectionChange}
					sectionPosition={sectionPosition}
					isLoading={dataIsLoading}
				/>
			)}
			<DataGridPro
				pagination
				pageSizeOptions={[10, 20, 50, 100]}
				dataSource={dataSource}
				initialState={{
					pagination: { paginationModel: { pageSize: 50, page: 0 }, rowCount: 0 },
				}}
				apiRef={apiRef}
				getRowId={dataGridType.getRowId || ((row) => row.id)}
				loading={dataIsLoading}
				slotProps={{
					loadingOverlay: {
						variant: 'linear-progress',
						noRowsVariant: 'skeleton',
					},
				}}
				onDataSourceError={(error: GridGetRowsError | GridUpdateRowError) => {
					const originalError = (error as any).cause || (error as any).originalError;
					dispatch(
						showAlert({
							message: originalError?.data?.message || 'An error occurred loading data.',
							type: 'error',
						})
					);
				}}
				columns={stableColumns}
				disableColumnFilter={true}
				checkboxSelection={checkboxEnabled}
				disableRowSelectionOnClick
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={handleRowSelectionModelChange}
				listView={isMobile}
				listViewColumn={mobileListViewColDef}
				rowHeight={isMobile ? 220 : 52}
				columnBufferPx={4}
				sx={(theme) => ({
					backgroundColor: theme.palette.background.paper,
					'& .MuiDataGrid-filler': {
						backgroundColor: `${theme.palette.background.paper} !important`,
					},
					'& .MuiDataGrid-columnHeaderTitle': {
						fontWeight: 'bold',
					},
					'& .MuiDataGrid-virtualScroller': {
						overflowX: 'hidden',
					},
					'& .MuiDataGrid-scrollbar--horizontal': {
						display: 'none',
					},
				})}
			/>
		</Box>
	);
};

export default DataGridProComponent;
